import {connect} from 'react-redux';
import {createSelector} from 'reselect';

import {Handler, HandlerOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {
  DEFAULT_SEARCH_FILTERS,
  findSelectedInboxesSearchLocation,
  selectedSearchFilterKey,
} from '../../../../../srcCommon/inboxes/search/searchHelpers';
import {makeMapStateToProps} from '../../../../helpers/reselectHelpers';
import {ConversationSortPropertiesEnum} from '../../../../models/api/conversationCommonTypes';
import {InboxesSearchLocationsEnum} from '../../../../models/url/inboxesUrlModel';
import {getInboxes} from '../../../../store/data/inboxes/inboxesSelectors';
import {getLenses} from '../../../../store/data/lenses/lensesSelectors';
import {updateSelectedFilterAction} from '../../../../store/data/localPreferences/localPreferencesActions';
import {getSelectedFilters} from '../../../../store/data/localPreferences/localPreferencesSelectors';
import {getStarredTagIds, getTags} from '../../../../store/data/tags/tagsSelectors';
import {getFindTeammate} from '../../../../store/data/teammates/lazyTeammatesSelectors';
import {getUserId} from '../../../../store/data/teammates/teammatesSelectors';
import {getTeams} from '../../../../store/data/teams/teamsSelectors';
import {
  CellsPanelPageSelector,
  makeGetCellsPanelCurrentPage,
} from '../../../../store/panels/cells/cellsPanelSelectors';
import {
  clearSearchHintsAction,
  maybeUpdateSearchSessionTimestampAction,
  saveRecentQueryAction,
  setHighlightAlgorithmAction,
} from '../../../../store/panels/cells/search/cellsPanelSearchActions';
import {
  getCellsSearchQueryHash,
  getCellsSearchResultsCounts,
} from '../../../../store/panels/cells/search/cellsPanelSearchSelectors';
import {HighlightAlgorithmsEnum} from '../../../../store/panels/cells/search/cellsPanelSearchState';
import {toggleSearchQueryBuilderDropdownAction} from '../../../../store/panels/inboxes/inboxesPanelActions';
import {
  InboxesPanelProps,
  InboxesParametersSelector,
  makeGetIsSearchQueryBuilderDropdownOpen,
} from '../../../../store/panels/inboxes/inboxesPanelSelectors';
import {
  InboxesParametersTypesEnum,
  InboxesSearchParameters,
} from '../../../../store/panels/inboxes/parameters/inboxesParameters';
import {buildParametersSearchLocalFilterName} from '../../../../store/panels/inboxes/parameters/inboxesParametersFiltersHelpers';
import {getParametersSearchLocalFilterIcon} from '../../../../store/panels/inboxes/parameters/inboxesParametersFiltersIconHelpers';
import {findParametersSearch} from '../../../../store/panels/inboxes/parameters/inboxesParametersHelpers';
import {InboxesPanelId, makeCellsPanelId} from '../../../../store/panels/panelStateBase';
import {searchByParametersAction} from '../../../../store/stateless/searchActions';
import {CellsHeaderFiltersProps, OnFilterSelectOption} from '../../cells/header/cellsHeaderFilters';
import {InboxesSearchBar} from './inboxesSearchBar';

/*
 * Props.
 */

interface InboxesSearchBarContainerProps {
  panelId: InboxesPanelId;
}

/*
 * Selectors.
 */

function getCellsPanelId(_state: unknown, props: InboxesSearchBarContainerProps) {
  return makeCellsPanelId(props.panelId);
}

function makeGetCellsContext<P>(getCellsPanelPage: CellsPanelPageSelector<P>) {
  return createSelector(getCellsPanelPage, (page) => page.cellsContext);
}

function makeGetCellsInboxesParameters<P>(getCellsPanelPage: CellsPanelPageSelector<P>) {
  return createSelector(getCellsPanelPage, (page) => page.parameters);
}

type SearchFilters = CellsHeaderFiltersProps<InboxesSearchLocationsEnum>;
function makeGetSearchFilters(getParameters: InboxesParametersSelector<InboxesPanelProps>) {
  return createSelector(
    [
      getParameters,
      getInboxes,
      getTags,
      getLenses,
      getStarredTagIds,
      getFindTeammate,
      getTeams,
      getSelectedFilters,
    ],
    (
      parameters,
      inboxes,
      tags,
      lenses,
      starredTagIds,
      findTeammate,
      teams,
      selectedFilters,
    ): SearchFilters => {
      const options = {
        shouldWrapAllNamesInQuotes: false,
        shouldWrapProperNounsInQuotes: true,
      };

      const localName = buildParametersSearchLocalFilterName(
        inboxes,
        tags,
        lenses,
        findTeammate,
        teams,
        parameters,
        options,
      );

      const localIcon = getParametersSearchLocalFilterIcon(
        inboxes,
        tags,
        lenses,
        starredTagIds,
        teams,
        findTeammate,
        parameters,
      );

      const search = findParametersSearch(parameters);

      return {
        parameters,
        filters: DEFAULT_SEARCH_FILTERS,
        selectedFilter: (search && search.location) || findSelectedInboxesSearchLocation(selectedFilters),
        selectedFilters: [(search && search.location) || findSelectedInboxesSearchLocation(selectedFilters)],
        localSearchFilterName: localName,
        localSearchFilterIcon: localIcon,
      };
    },
  );
}

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<InboxesSearchBarContainerProps>()(() => {
  const getCellsPanelCurrentPage = makeGetCellsPanelCurrentPage(getCellsPanelId);
  const getInboxesParameters = makeGetCellsInboxesParameters(getCellsPanelCurrentPage);
  const getIsSearchQueryBuilderDropdownOpen = makeGetIsSearchQueryBuilderDropdownOpen();

  return {
    userId: getUserId,

    parameters: getInboxesParameters,
    cellsContext: makeGetCellsContext(getCellsPanelCurrentPage),
    searchFilters: makeGetSearchFilters(getInboxesParameters),
    queryHash: getCellsSearchQueryHash,
    isSearchQueryBuilderDropdownOpen: getIsSearchQueryBuilderDropdownOpen,
    searchResultsCounts: getCellsSearchResultsCounts,
  };
});

export const InboxesSearchBarContainer = connect(
  mapStateToProps,
  {
    searchByParametersAction,
    updateSelectedFilterAction,

    saveRecentQueryAction,
    clearSearchHintsAction,
    maybeUpdateSearchSessionTimestampAction,

    toggleSearchQueryBuilderDropdownAction,

    setHighlightAlgorithmAction,
  },
  (stateProps, dispatchProps, ownProps) => {
    const {parameters} = stateProps;

    const onFilterSelect = (filter: InboxesSearchLocationsEnum, options: OnFilterSelectOption) => {
      // Filters only apply to conversation lists.
      if (parameters.type === InboxesParametersTypesEnum.TEAMMATE_MESSAGES) {
        return;
      }

      // Otherwise, find the search parameters.
      const {search} = parameters;
      if (!search) {
        return;
      }

      // Save chosen filter in localStorage.
      if (options.shouldPersistSelectedFilter) {
        dispatchProps.updateSelectedFilterAction(selectedSearchFilterKey, filter);
      }

      // Navigate to the new search location.
      dispatchProps.searchByParametersAction({...search, location: filter}, stateProps.cellsContext);
    };

    // Search.
    const navigateToSearch = (search: InboxesSearchParameters | undefined, wasQueryPasted?: boolean) => {
      if (search) {
        dispatchProps.updateSelectedFilterAction(selectedSearchFilterKey, search.location);
      }

      dispatchProps.searchByParametersAction(
        search,
        stateProps.cellsContext,
        {isPasted: wasQueryPasted},
        ConversationSortPropertiesEnum.BUMPED_AT,
      );
    };

    const saveRecentQuery: HandlerOf<string> = (query) => dispatchProps.saveRecentQueryAction(query);
    const clearSearchHints: Handler = () => dispatchProps.clearSearchHintsAction();
    const maybeUpdateSearchSessionTimestamp: Handler = () =>
      dispatchProps.maybeUpdateSearchSessionTimestampAction();

    const toggleSearchQueryBuilderDropdown: Handler = () =>
      dispatchProps.toggleSearchQueryBuilderDropdownAction(ownProps.panelId, 'search-bar');

    const setSelectedHighlightAlgorithm: HandlerOf<HighlightAlgorithmsEnum> = (algorithm) =>
      dispatchProps.setHighlightAlgorithmAction(algorithm);

    return {
      ...ownProps,
      ...stateProps,
      onFilterSelect,
      navigateToSearch,
      saveRecentQuery,
      clearSearchHints,
      maybeUpdateSearchSessionTimestamp,
      toggleSearchQueryBuilderDropdown,
      setSelectedHighlightAlgorithm,
    };
  },
)(InboxesSearchBar);
